import type { ZodTypeAny } from 'zod';
import type
{
    AgentToolsHandler,
    AgentToolsInternal,
    AgentToolsRegistrationOptions,
    AgentToolsToolRegistrationOptions
} from '#layers/core/app/runtime/types/tools';

// Kontrakt rejestru moduł dostaje podany z zewnątrz (plugins/tools.ts) i trzyma w referencji
// modułowej, zamiast czytać go w funkcjach rejestrujących przez useNuxtApp(). Rejestracja bywa
// wołana z onMounted komponentu, z setupu innego pluginu i z testów jednostkowych chodzących bez
// Nuxta (projekt `unit`, environment `node`) - w dwóch ostatnich przypadkach useNuxtApp() nie ma
// czego zwrócić, więc odczyt kontraktu nie może od niego zależeć
//
// Referencja modułowa jest tu bezpieczna, bo panel jest aplikacją jednostronicową (`ssr: false`
// w layers/core/nuxt.config.ts) - moduł żyje w jednej karcie jednego użytkownika i nie ma żądań,
// między którymi ten stan mógłby przeciekać. W projekcie renderowanym po stronie serwera ten sam
// wzorzec oddawałby rejestr jednego użytkownika żądaniu innego - nie przenoś go tam
let contract: AgentToolsInternal | null = null;

export function setAgentToolsRegistry(tools: AgentToolsInternal): void
{
    contract = tools;
}

// Brak rejestru kończy się wyjątkiem, a nie pominięciem rejestracji: dotychczasowe
// `window.edito?.tools?.` po cichu nic nie rejestrowało, więc widok wyglądał na sprawny, choć agent
// nie miał ani jednego narzędzia - a taka pomyłka jest w kolejności ładowania pluginów, nie
// w danych, i musi się zgłosić od razu
function registry(): AgentToolsInternal
{
    if (contract === null)
    {
        throw new Error('Rejestr narzędzi agenta nie jest gotowy - rejestracja musi nastąpić po pluginie `tools`.');
    }

    return contract;
}

export function registerTool(name: string, description: string, schema: ZodTypeAny, handler: AgentToolsHandler, options: AgentToolsToolRegistrationOptions): void
{
    registry().registerTool(name, description, schema, handler, options);
}

export function registerPrompt(name: string, description: string, handler: AgentToolsHandler, options: AgentToolsRegistrationOptions): void
{
    registry().registerPrompt(name, description, handler, options);
}

export function registerResource(name: string, description: string, handler: AgentToolsHandler, options: AgentToolsRegistrationOptions): void
{
    registry().registerResource(name, description, handler, options);
}

// Nazwa z przedrostkiem, bo `runtime/utils` jest katalogiem auto-importów Nuxta (layers/core/nuxt.config.ts)
// i każdy eksport stąd wchodzi do globalnej przestrzeni całego panelu - samo `unregister` zderzałoby
// się w czytaniu z lokalnymi funkcjami o tej nazwie w komponentach (np. filtry, pasek edytora)
//
// Brak gotowego rejestru kończy się wyjątkiem, tak samo jak przy rejestracji: pomyłka w kolejności
// ładowania pluginów jest błędem w kodzie i ma się zgłosić od razu. Cicha zgoda dotyczy wyłącznie
// nazwy, której nie ma w gotowym rejestrze - to rozstrzyga sam rejestr
export function unregisterAgentTool(name: string): void
{
    registry().unregister(name);
}
